import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { signOut } from 'next-auth/react'

dayjs.extend(utc)

export const TEMP_PREFIX = 'temp_' as const

export function isTempFile(fileId: string): boolean {
	return fileId.startsWith(TEMP_PREFIX)
}

export function toSentenceCase(text: string | null | undefined): string {
	if (!text) return ''
	const result = text
		.replace(/([A-Z])/g, ' $1')
		.toLowerCase()
		.replace(/_/g, ' ')
		.trim()
	return result[0]?.toUpperCase() + result.substring(1).toLowerCase()
}
export function toTitleCase(text: string | null | undefined): string {
	if (!text) return ''

	return text
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/[_\s]+/g, ' ')
		.trim()
		.split(' ')
		.filter(Boolean)
		.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ')
}

export function snakeCaseToSentence(text: string): string {
	return text.replace(/^[-_]*(.)/, (_, c) => c.toUpperCase()).replace(/[-_]+(.)/g, (_, c) => ` ${c.toUpperCase()}`)
}

export function camelCaseToSentence(text: string): string {
	const result = text.replace(/([A-Z])/g, ' $1')
	return result.charAt(0).toUpperCase() + result.slice(1)
}

export function toCamelCase(text: string): string {
	if (!text) return ''
	return text.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
}

export function toSnakeCase(text: string): string {
	return text
		.replace(/([a-z])([A-Z])/g, '$1_$2')
		.replace(/\s+/g, '_')
		.toLowerCase()
}

export function sanitizeFields<T extends Record<string, unknown>>(data: T): T {
	const newData: T = {} as T

	for (const [key, value] of Object.entries(data)) {
		if (typeof value === 'string') {
			newData[key as keyof T] = value.trim() as T[keyof T]
			continue
		}
		newData[key as keyof T] = value as T[keyof T]
	}

	return newData
}

export function booleanToYesNo(value: boolean | null | undefined): string {
	return value ? 'Yes' : 'No'
}

export function removeSecondsFromTime(dateString: string | undefined): string {
	if (!dateString) return ''
	const time = dateString.split(':')
	const secondsWithAmPm = `:${time.at(-1)}`
	const amPm = secondsWithAmPm?.split(' ').at(-1) as string
	return dateString.replace(secondsWithAmPm, ` ${amPm}`)
}

export async function apiClient(apiEndpoint: string, apiKey: string, requestOpts: RequestInit = {}): Promise<Response> {
	if (!apiKey) {
		void signOut()
		return Promise.reject()
	}

	return fetch(apiEndpoint, {
		...requestOpts,
		headers: { ...requestOpts.headers, Authorization: `Bearer ${apiKey}` },
	})
}

export async function fetchResource<T>(apiEndpoint: string, defaultValue: T, apiKey: string, requestOpts: RequestInit = {}): Promise<T> {
	const result = await apiClient(apiEndpoint, apiKey, requestOpts)
	return result.ok ? result.json() : defaultValue
}

export function createApi(apiKey: string) {
	return {
		apiClient: (apiEndpoint: string, requestOpts: RequestInit = {}) => apiClient(apiEndpoint, apiKey, requestOpts),
		fetchResource: <T>(apiEndpoint: string, defaultValue: T, requestOpts: RequestInit = {}) =>
			fetchResource<T>(apiEndpoint, defaultValue, apiKey, requestOpts),
	}
}

export function stringToArray(str: string): string[] {
	return str?.replace('[', '').replace(']', '').split(',') ?? []
}

export function arrayToString(arr: unknown[]): string {
	return `[${arr.toString()}]`
}

export function omit<T extends Record<string, unknown>, K extends keyof T>(obj: T, ...keys: K[]) {
	return Object.fromEntries(Object.entries(obj).filter(([key]) => !keys.includes(key as K))) as Omit<T, K>
}

export function transformObjectValuesToString(object: Record<string, unknown>): Record<string, string> {
	return Object.entries(object).reduce((acc, [currKey, currVal]) => {
		acc[currKey] = String(currVal)
		return acc
	}, {} as Record<string, string>)
}

export function objectToMap<T>(obj: Record<string, T>): Map<string, T> {
	return Object.entries(obj).reduce((acc, [key, val]) => {
		acc.set(key, val)
		return acc
	}, new Map())
}

export function sortBy<T extends Record<string, unknown>>(array: T[], sortKey: keyof T): T[] {
	return array.sort((a, b) => {
		if (a[sortKey] < b[sortKey]) return -1
		if (a[sortKey] > b[sortKey]) return 1
		return 0
	})
}

export function formatBytes(value: number): string {
	const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
	let l = 0
	let n = value ?? 0
	while (n >= 1024 && ++l) {
		n = n / 1024
	}
	return `${n.toFixed(n < 10 && l > 0 ? 1 : 0)} ${units[l]}`
}

export const alphabeticalWithSpaceRulePattern = {
	value: /^[A-Za-z\s]+$/,
	message: 'Only alphabetical characters and spaces are allowed',
}

export function generateRandomString(length: number): string {
	const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
	const randomString = Array.from({ length }, () => characters.charAt(Math.floor(Math.random() * characters.length))).join('')
	return randomString
}

export function chunkString(str: string, size: number): string[] {
	const numChunks = Math.ceil(str.length / size)
	const chunks = new Array(numChunks)

	for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
		chunks[i] = str.substring(o, o + size)
	}

	return chunks
}

export function getImageUrlFromArrayBuffer(arrayBuffer: ArrayBuffer): string {
	return URL.createObjectURL(new Blob([arrayBuffer], { type: 'application/octet-stream' }))
}

export function replaceLastOccurrence(text: string, search: string, replacement: string): string {
	const lastIndex = text.lastIndexOf(search)
	if (lastIndex === -1) return text

	const start = text.substring(0, lastIndex)
	const end = text.substring(lastIndex)

	const replacedEnd = end.replace(search, replacement)

	return start + replacedEnd
}

export function calculateImageDimensions(
	imageSrc: string,
): Promise<{ width: number; height: number; ratio: number; isHeightBigger: boolean }> {
	return new Promise((resolve, reject) => {
		const img = new Image()
		img.src = imageSrc

		img.onload = () => {
			const height = img.height
			const width = img.width
			const isHeightBigger = height > width
			const ratio = isHeightBigger ? width / height : height / width
			resolve({ width, height, ratio, isHeightBigger })
		}

		img.onerror = () => {
			reject(new Error('Error on load image.'))
		}
	})
}

// formatDateISO displays the date exactly as stored in the database without timezone adjustments
// Use this when you want to show the date exactly as it is in the database (e.g., due dates, promise dates)
export function formatDateISO(date: unknown, defaultEmptyValue = ''): string {
	if (!date) return defaultEmptyValue

	let dateObj: Date
	if (typeof date === 'string') {
		dateObj = new Date(date)
	} else if (dayjs.isDayjs(date)) {
		dateObj = date.toDate()
	} else if (date instanceof Date) {
		dateObj = date
	} else {
		return defaultEmptyValue
	}

	if (isNaN(dateObj.getTime())) return defaultEmptyValue
	const [datePart] = dateObj.toISOString().split('T')
	const d = dayjs(datePart)
	return d.format('DD MMM YYYY').toUpperCase()
}

// formatDate displays the date in the user's local timezone
// Use this when you want to show the date relative to the user's timezone (e.g., creation dates, modification dates)
export function formatDate(date: Date | string | null, defaultEmptyValue = ''): string {
	if (!date) return defaultEmptyValue
	const dateObj = typeof date === 'string' ? new Date(date) : date
	if (isNaN(dateObj.getTime())) return defaultEmptyValue
	return dayjs(date).format('DD MMM YYYY').toUpperCase()
}

export function cleanAlphanumericString(input: string): string {
	return input
		.trim()
		.replace(/[^a-zA-Z0-9 ]/g, '') // Remove non-alphanumeric characters
		.replace(/\s+/g, ' ') // Replace multiple spaces with a single space
		.toLowerCase()
}

export function numberToMoney(value: number): string {
	return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

export function toStringOrEmpty(value: unknown): string {
	return value?.toString() ?? ''
}

export function formatNumber(value: number | null | undefined, decimals = 1): string {
	if (value == null) return ''
	return value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

export function isNullOrEmpty(value: unknown): boolean {
	if (value === null || value === undefined) return true
	if (typeof value === 'string' && value.trim() === '') return true
	if (Array.isArray(value) && value.length === 0) return true
	if (typeof value === 'object' && Object.keys(value).length === 0) return true
	return false
}

export function isValidDate(value: unknown): boolean {
	if (value instanceof Date) {
		return !isNaN(value.getTime())
	}

	if (dayjs.isDayjs(value)) {
		return !isNaN(value.toDate().getTime())
	}

	if (typeof value === 'string') {
		const parsed = new Date(value)
		return !isNaN(parsed.getTime())
	}

	return false
}

export function validateObjectKeys(object: Record<string, unknown>, expectedObject: string[]): string[] {
	return expectedObject.filter(key => !Object.keys(object).includes(key))
}

export function valueFormatterDataGrid(value: unknown) {
	if (typeof value === 'boolean') return booleanToYesNo(value)
	if (value instanceof Date) {
		const date = new Date(value)
		return isNaN(date.getTime()) ? value : date.toLocaleDateString()
	}
	if (value === 0) return value
	return value || '-'
}

export function dateToStringUTC(date?: Date): string {
	return `${date?.toLocaleString('en-US', { timeZone: 'UTC', hour12: false })} (UTC)`
}

export function extractDateOnlyString(date: Date): string {
	return date.toISOString().split('T')[0]
}

export function normalizeDate(date: Date): Date {
	if (!isValidDate(date)) return dayjs().startOf('day').toDate()
	const dateString = extractDateOnlyString(date)
	return dayjs(dateString).startOf('day').toDate()
}

export function normalizeDateToSave(date: Date): Date {
	return dayjs(date).utc().startOf('day').toDate()
}

export function isDateInPast(date: Date): boolean {
	const today = normalizeDate(dayjs().startOf('day').toDate())
	const normalizedDate = normalizeDate(new Date(date))
	return normalizedDate < today
}

export function downloadFileFromBlob(blob: Blob, fileName: string): void {
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = fileName
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

/**
 * Moves an element within an array from one index to another.
 *
 * Mutates the original array by removing the element at the `from` index
 * and inserting it at the `to` index, shifting other elements as needed.
 *
 * @template T - The type of elements in the array.
 * @param {T[]} arr - The array to be modified.
 * @param {number} from - The current index of the element to move.
 * @param {number} to - The target index where the element should be moved.
 * @returns {void}
 */
export function moveOnArray<T>(arr: T[], from: number, to: number): void {
	if (from === to) return
	const item = arr.splice(from, 1)[0]
	arr.splice(to, 0, item)
}

const GOLDEN_RATIO = 0.618033988749895

export function stringToHash(str: string): number {
	let hash = 0
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i)
		hash = (hash << 5) - hash + char
		hash = hash & hash
	}
	return Math.abs(hash)
}

export function generateColorByName(name: string, index: number): string {
	const hash = stringToHash(name)
	const hashSeed = (hash % 100) / 100

	const baseHue = (index * GOLDEN_RATIO + hashSeed * 0.2) % 1
	const hue = Math.floor(baseHue * 360)

	const saturation = 70 + ((hash % 10) - 5)
	const lightness = 55 + ((hash % 10) - 5)

	return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

/**
 * Retries an asynchronous operation with exponential backoff on failure.
 */
export async function retryOperation<T>(
	operation: () => Promise<T>,
	retries = 3,
	delay = 1000,
	onRetry?: (retriesLeft: number, error: unknown) => void,
): Promise<T> {
	try {
		return await operation()
	} catch (error) {
		if (retries > 0) {
			onRetry?.(retries - 1, error)
			await new Promise(resolve => setTimeout(resolve, delay))
			return retryOperation(operation, retries - 1, delay * 2, onRetry)
		}
		throw error
	}
}

export function timeZoneName(): string {
	return new Date().toLocaleTimeString('en', { timeZoneName: 'short' }).split(' ').pop() ?? ''
}

export function formatDateWithTimeZone(date: Date): string {
	return `${dayjs(date).format('DD MMM YYYY HH:mm:ss')}. ${timeZoneName()}`.toUpperCase()
}

export const isDateChanged = (originalDate: Date | null | undefined, newDate: Date | null | undefined) => {
	if (!originalDate && !newDate) return false
	if (!originalDate || !newDate) return true
	const normalizedOriginal = normalizeDate(originalDate)
	const normalizedNew = normalizeDate(newDate)
	return !dayjs(normalizedOriginal).isSame(dayjs(normalizedNew))
}

export function isValidEmail(value: unknown): boolean {
	if (!value) return true
	return /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(String(value))
}

export function resolveNotificationRecipients(params: {
	customerLevel: string | null | undefined
	pocEmail: string | null | undefined
	salesEngineerEmail: string | null | undefined
	defaultTo: string | string[]
}): { to: string | string[]; cc?: string[] } {
	const pocEmail = params.pocEmail?.trim() ?? ''
	const isInternalCustomer = params.customerLevel?.trim().toLowerCase() === 'internal'
	const hasInternalPocDomain = ['@resonetics.com', '@resolutionmedical.com'].some(domain => pocEmail.toLowerCase().endsWith(domain))

	if (isInternalCustomer && pocEmail && hasInternalPocDomain) {
		return {
			to: pocEmail,
			...(params.salesEngineerEmail?.trim() && { cc: [params.salesEngineerEmail.trim()] }),
		}
	}

	return { to: params.defaultTo }
}
